import { Box, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Divider, Grid2, IconButton, Typography } from "@mui/material";
import { GISTMCheckFiles, GISTMChecklist } from "../../services/api/gistm";
import { useState } from "react";
import { RequestError, useApi } from "../../services";
import { ErrorOnDialog, LoadingModal, RemoveButton } from "../modals";
import { objectToHash } from "../../core";
import { FileUpload } from "../fileUpload";
import { Description, Download, Image, InsertDriveFile, PictureAsPdf } from "@mui/icons-material";

export interface GISTMCheckFileProps extends DialogProps {
  onFinish?: (files: GISTMCheckFiles[]) => void;
  checkData: GISTMChecklist;
}

export const GISTMCheckFileModal: React.FC<GISTMCheckFileProps> = ({ checkData, onFinish, ...dialogProps }) => {
  const api = useApi();

  const [filesAdd, setFilesAdd] = useState<File[]>();
  const [error, setError] = useState<RequestError>();
  const [progress, setProgress] = useState<number>();
  const [files, setFiles] = useState<GISTMCheckFiles[]>(checkData.files);

  const onAddFile = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, files: File[]) => {
    ev.preventDefault();
    ev.stopPropagation();

    const addFiles = async () => {
      setProgress(0);
      for (const file of files) {
        const resp = await api.gistm.setChecklistFile(checkData.id, file, setProgress);
        if ("error" in resp) {
          setError(resp);
          break;
        }
        setFiles(prev => [...prev, resp]);
      }
      setProgress(undefined);
      setFilesAdd(undefined);
    };

    addFiles();
  };

  const onFinalize = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.stopPropagation();
    ev.preventDefault();

    onFinish?.(files);
  };

  const onRemove = (fileItem: GISTMCheckFiles) => {
    api.gistm.removeChecklistFile(fileItem.id).then(value => {
      if ("error" in value) {
        setError(value);
        return;
      }
      setFiles(prev => prev.filter(val => val.id !== fileItem.id));
    });
  };

  return <>

    {error && <ErrorOnDialog
      open={Boolean(error)}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    {progress && <LoadingModal
      open={Boolean(progress)}
      message={`Upload ${progress.toFixed(0)}%`}
    />}

    <Dialog {...dialogProps}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="dialog-title">
        Adicionar Arquivos
      </DialogTitle>

      <DialogContent>
        <Divider />
        <div style={{ height: "1em" }} />

        {/* Arquivos e Upload */}
        <Box sx={{
          flex: 1,
          gap: "15px",
          display: "flex",
          maxHeight: "40vh",
          flexDirection: "row",
        }}>

          {/* Arquivos */}
          <Grid2 container sx={{ width: "60%", overflowY: "auto" }}>
            {files.map((item) => {
              return <Grid2
                key={objectToHash(item)}
                size={5}
                sx={{
                  p: "15px",
                  margin: "5px",
                  display: "flex",
                  borderRadius: 2,
                  textAlign: "center",
                  alignItems: "center",
                  boxSizing: "border-box",
                  flexDirection: "column",
                  border: "1px solid #ddd",
                }}
              >
                <Box sx={{ fontSize: 40 }}>{getFileIcon(item.fileExt)}</Box>

                <Typography>
                  {item.filename.substring(0, 3)}...{item.filename.substring(item.filename.length - 3, item.filename.length)}.{item.fileExt}
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    color="primary"
                    onClick={() => window.open(item.file, "_blank")}
                    title="Baixar arquivo"
                  >
                    <Download />
                  </IconButton>

                  <RemoveButton
                    title="Deseja excluir este arquivo?"
                    message="Esta ação é irreversível!"
                    onRemove={() => onRemove(item)}
                  />
                </Box>
              </Grid2>;
            })}
          </Grid2>

          {/* Upload */}
          <Box sx={{
            flex: 1,
            gap: "15px",
            display: "flex",
            flexDirection: "column",
          }}>
            <FileUpload
              multiple
              sx={{ flex: 1 }}
              files={filesAdd}
              onAddFile={(files) => setFilesAdd(files)}
            />

            {(filesAdd?.length ?? 0) > 0 && <Button
              variant="contained"
              onClick={(ev) => onAddFile(ev, filesAdd ?? [])}
            >
              Adicionar Arquivos
            </Button>}
          </Box>
        </Box>

      </DialogContent>

      <DialogActions>
        <Button onClick={onFinalize} autoFocus>Finalizar</Button>
      </DialogActions>
    </Dialog>
  </>;
};

function getFileIcon(ext: string) {
  const lower = ext.toLowerCase();
  if (["pdf"].includes(lower)) return <PictureAsPdf color="error" />;
  if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(lower))
    return <Image color="primary" />;
  if (["doc", "docx", "odt"].includes(lower)) return <Description color="info" />;
  if (["xls", "xlsx", "csv"].includes(lower)) return <Description color="success" />;
  if (["txt", "json", "xml", "log"].includes(lower)) return <Description color="secondary" />;
  return <InsertDriveFile color="disabled" />;
}